import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { isClickedOutside } from '@theme/utilities';

const STORAGE_KEY = 'littlemuslim-subscribe-popup';

/**
 * Timed email subscribe popup with localStorage dismiss tracking.
 * Also opens from any [data-open-subscribe-popup] trigger (Join buttons).
 *
 * @extends {DialogComponent}
 */
class SubscribePopupComponent extends DialogComponent {
  /** @type {number | undefined} */
  #timer;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

    document.addEventListener('click', this.#handleTriggerClick, { signal });

    if (this.dataset.enabled !== 'true') return;
    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    if (this.dataset.showOnLoad === 'true') {
      this.showDialog();
      if (this.dataset.showSuccess === 'true') {
        this.#markDismissed();
      }
      return;
    }

    if (this.#shouldForceShow()) {
      this.showDialog();
      return;
    }

    if (this.#isDismissed()) return;

    const delay = Number(this.dataset.delay || 2) * 1000;
    this.#timer = window.setTimeout(() => this.showDialog(), delay);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    super.disconnectedCallback();
    if (this.#timer) window.clearTimeout(this.#timer);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-open-subscribe-popup], a[href="#subscribe-popup"]');
    if (!trigger) return;

    event.preventDefault();
    this.showDialog();
  };

  #handleDialogClose = () => {
    if (this.dataset.showSuccess !== 'true') {
      this.#markDismissed();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  closeDialogOnClickOutside(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  closeDialogOnEscapePress(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  }

  #markDismissed() {
    const days = Number(this.dataset.hideDays || 7);
    const until = Date.now() + days * 24 * 60 * 60 * 1000;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ until }));
    } catch {
      // Ignore storage errors (private browsing, etc.)
    }
  }

  #isDismissed() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;

      const { until } = JSON.parse(raw);
      if (typeof until !== 'number') return false;

      if (Date.now() < until) return true;

      localStorage.removeItem(STORAGE_KEY);
      return false;
    } catch {
      return false;
    }
  }

  #shouldForceShow() {
    try {
      return new URLSearchParams(window.location.search).has('subscribe_popup');
    } catch {
      return false;
    }
  }
}

if (!customElements.get('subscribe-popup-component')) {
  customElements.define('subscribe-popup-component', SubscribePopupComponent);
}
